import { useEffect, useMemo, useState } from "react";
import SharedAddressNotice from "@/components/SharedAddressNotice";
import { Link, useSearchParams } from "react-router-dom";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type {
  BulkFlowResult,
  PageResult,
  ProspectDetail,
  ProspectFieldView,
  ProspectSchemaView,
  ProspectStatus,
  ProspectSummary,
  StrategyDetail,
  StrategySummary,
} from "@/lib/types";
import StatusBadge from "@/components/StatusBadge";
import { FlowStateBadge } from "@/components/EmailStatusBadge";
import LeadImportDialog from "@/components/LeadImportDialog";
import { formatDateTime } from "@/lib/format";
import { usePersistedState } from "@/lib/persistedState";
import FilterPresets from "@/components/FilterPresets";

const STATUS_OPTIONS: (ProspectStatus | "ALL")[] = [
  "ALL", "NEW", "CONTACTED", "ENGAGED", "QUALIFIED",
  "NEGOTIATING", "CLOSING", "WON", "LOST",
  "UNRESPONSIVE", "RE_ENGAGEMENT", "ARCHIVED",
];

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: "createdAt,desc", label: "Newest first" },
  { value: "createdAt,asc", label: "Oldest first" },
  { value: "lastActivityAt,desc", label: "Recent activity" },
  { value: "email,asc", label: "Email A→Z" },
  { value: "status,asc", label: "Status A→Z" },
];

/**
 * The selections we hand back to a returning user, and what a saved preset
 * holds. Dropdowns only: the search box takes free text that can name a
 * prospect, and prospect data does not get written to a viewer's disk.
 */
type StoredFilters = {
  status: ProspectStatus | "ALL";
  strategyId: string;
  phaseId: string;
  sort: string;
};

const DEFAULT_FILTERS: StoredFilters = {
  status: "ALL", strategyId: "", phaseId: "", sort: "createdAt,desc",
};

/**
 * Anything in storage we cannot render as a selected option becomes the
 * unfiltered choice, so a stale key can never wedge the page into a filter the
 * UI has no way to show or clear. Strategy and phase ids are checked against
 * the live lists once those have loaded (see the effects in the page).
 */
function reviveFilters(stored: unknown): StoredFilters | null {
  if (typeof stored !== "object" || stored === null) return null;
  const raw = stored as Record<string, unknown>;
  const status = typeof raw.status === "string" && (STATUS_OPTIONS as string[]).includes(raw.status)
    ? (raw.status as ProspectStatus | "ALL")
    : "ALL";
  const sort = typeof raw.sort === "string" && SORT_OPTIONS.some((o) => o.value === raw.sort)
    ? raw.sort
    : DEFAULT_FILTERS.sort;
  return {
    status,
    strategyId: typeof raw.strategyId === "string" ? raw.strategyId : "",
    phaseId: typeof raw.phaseId === "string" ? raw.phaseId : "",
    sort,
  };
}

export default function ProspectsPage() {
  // The dropdown filters persist across visits, and a URL deep link overrides
  // the remembered strategy + phase so other pages can point into a filtered
  // list (the pipeline board's "View all" links pass strategyId + phaseId; its
  // empty state passes import=1). The link is applied once, when the filter
  // state is first created; from then on the filters are local state that the
  // page remembers but does not write back to the URL.
  const [searchParams] = useSearchParams();
  const linkedStrategyId = searchParams.get("strategyId");
  const linkedPhaseId = searchParams.get("phaseId");
  const withDeepLink = (filters: StoredFilters): StoredFilters =>
    linkedStrategyId
      ? { ...filters, strategyId: linkedStrategyId, phaseId: linkedPhaseId ?? "" }
      : filters;
  const [filters, setFilters] = usePersistedState<StoredFilters>(
    "prospects:filters",
    withDeepLink(DEFAULT_FILTERS),
    (stored) => {
      const revived = reviveFilters(stored);
      return revived === null ? null : withDeepLink(revived);
    },
  );
  const { status, strategyId, phaseId, sort } = filters;
  const [page, setPage] = useState(0);
  const [size] = useState(25);
  const [q, setQ] = useState("");

  function applyFilters(patch: Partial<StoredFilters>) {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(0);
  }

  const strategiesQ = useQuery({
    queryKey: ["strategies"],
    queryFn: () => api.get<StrategySummary[]>("/api/v1/strategies"),
  });
  // The list filter works on strategy ids (what the prospects API takes), but
  // phases hang off the slug-addressed detail endpoint — map via the list.
  const selectedStrategy = strategiesQ.data?.find((s) => s.id === strategyId) ?? null;

  const strategyDetailQ = useQuery({
    queryKey: ["strategy-detail", selectedStrategy?.slug],
    enabled: !!selectedStrategy,
    queryFn: () => api.get<StrategyDetail>(`/api/v1/strategies/${selectedStrategy?.slug}`),
  });

  // A remembered strategy can be deleted between visits, and a remembered
  // phase can be removed from its strategy. Left in place either renders as a
  // blank dropdown that silently filters every prospect away.
  const strategyList = strategiesQ.data;
  useEffect(() => {
    if (!strategyId || !strategyList) return;
    if (!strategyList.some((s) => s.id === strategyId)) {
      applyFilters({ strategyId: "", phaseId: "" });
    }
    // applyFilters is a stable closure over setFilters/setPage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyId, strategyList]);
  const phaseList = strategyDetailQ.data?.phases;
  useEffect(() => {
    if (!phaseId || !phaseList) return;
    if (!phaseList.some((ph) => ph.id === phaseId)) {
      applyFilters({ phaseId: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseId, phaseList]);

  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("size", String(size));
  params.set("sort", sort);
  if (status !== "ALL") params.set("status", status);
  if (strategyId) params.set("strategyId", strategyId);
  if (phaseId) params.set("phaseId", phaseId);
  if (q.trim()) params.set("q", q.trim());

  const { data, isLoading, error } = useQuery({
    queryKey: ["prospects", params.toString()],
    queryFn: () => api.get<PageResult<ProspectSummary>>(`/api/v1/prospects?${params.toString()}`),
    placeholderData: keepPreviousData,
  });

  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(searchParams.get("import") === "1");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  const visibleIds = data?.items.map((p) => p.id) ?? [];
  const selectedIds = visibleIds.filter((id) => selected.has(id));

  // Selection is scoped to what is on screen. Carrying it across pages would
  // let an operator act on rows they can no longer see, which for an action
  // that starts contacting people is not a reasonable surprise.
  useEffect(() => {
    setSelected(new Set());
  }, [params.toString()]);

  const bulk = useMutation({
    mutationFn: ({ action, ids }: { action: "start" | "pause" | "stop"; ids: string[] }) =>
      api.post<BulkFlowResult>(`/api/v1/prospects/bulk/${action}`, { prospectIds: ids }),
    onSuccess: () => {
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ["prospects"] });
    },
  });

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Prospects</h1>
          <p className="text-sm text-[var(--fg-muted)]">
            {data ? `${data.total.toLocaleString()} total` : "Loading…"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImport(true)}
            className="rounded-md border border-[var(--border)] bg-white px-3 py-1.5 text-sm hover:bg-zinc-50"
          >Import list</button>
          <button
            onClick={() => setShowCreate(true)}
            className="rounded-md bg-blue-600 text-white px-3 py-1.5 text-sm hover:bg-blue-700"
          >New prospect</button>
        </div>
      </header>

      {showCreate ? (
        <CreateProspectModal onClose={() => setShowCreate(false)} />
      ) : null}
      {showImport ? (
        <LeadImportDialog onClose={() => setShowImport(false)} />
      ) : null}

      <div className="flex flex-wrap gap-3 items-end">
        <label className="text-sm">
          <div className="text-xs text-[var(--fg-muted)] mb-1">Search</div>
          <input
            type="search"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            placeholder="email or name"
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
          />
        </label>
        <label className="text-sm">
          <div className="text-xs text-[var(--fg-muted)] mb-1">Status</div>
          <select
            value={status}
            onChange={(e) => applyFilters({ status: e.target.value as ProspectStatus | "ALL" })}
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm bg-white"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <div className="text-xs text-[var(--fg-muted)] mb-1">Strategy</div>
          <select
            value={strategyId}
            onChange={(e) =>
              // Phases belong to one strategy; a stale phase filter under a
              // different strategy would silently match nothing.
              applyFilters({ strategyId: e.target.value, phaseId: "" })
            }
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm bg-white"
          >
            <option value="">All strategies</option>
            {strategiesQ.data?.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </label>
        {strategyId ? (
          <label className="text-sm">
            <div className="text-xs text-[var(--fg-muted)] mb-1">Phase</div>
            <select
              value={phaseId}
              onChange={(e) => applyFilters({ phaseId: e.target.value })}
              className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm bg-white"
            >
              <option value="">All phases</option>
              {strategyDetailQ.data?.phases.map((ph) => (
                <option key={ph.id} value={ph.id}>{ph.name}</option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="text-sm">
          <div className="text-xs text-[var(--fg-muted)] mb-1">Sort</div>
          <select
            value={sort}
            onChange={(e) => applyFilters({ sort: e.target.value })}
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm bg-white"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <div className="ml-auto">
          <FilterPresets<StoredFilters>
            storageKey="prospects"
            current={filters}
            revive={reviveFilters}
            onApply={(next) => { setFilters(next); setPage(0); }}
          />
        </div>
      </div>

      {selectedIds.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[var(--border)] bg-zinc-50 px-4 py-2 text-sm">
          <span className="font-medium">{selectedIds.length} selected</span>
          <button
            type="button"
            disabled={bulk.isPending}
            onClick={() => bulk.mutate({ action: "start", ids: selectedIds })}
            className="rounded-md bg-emerald-600 px-3 py-1 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            Start flow
          </button>
          <button
            type="button"
            disabled={bulk.isPending}
            onClick={() => bulk.mutate({ action: "pause", ids: selectedIds })}
            className="rounded-md border border-[var(--border)] bg-white px-3 py-1 hover:bg-zinc-50 disabled:opacity-50"
          >
            Pause
          </button>
          <button
            type="button"
            disabled={bulk.isPending}
            onClick={() => bulk.mutate({ action: "stop", ids: selectedIds })}
            className="rounded-md border border-[var(--border)] bg-white px-3 py-1 hover:bg-zinc-50 disabled:opacity-50"
          >
            Stop
          </button>
          {bulk.data ? (
            <span className="text-xs text-[var(--fg-muted)]">
              {bulk.data.accepted} applied
              {bulk.data.refused > 0 ? `, ${bulk.data.refused} skipped` : ""}
              {bulk.data.refused > 0
                ? ` (${
                    bulk.data.results.find((r) => r.refusedReason)?.refusedReason ?? "not eligible"
                  })`
                : ""}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-card)] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-[var(--fg-muted)] bg-zinc-50 border-b border-[var(--border)]">
            <tr>
              <th className="px-3 py-2.5 w-8">
                <input
                  type="checkbox"
                  aria-label="Select all on this page"
                  checked={visibleIds.length > 0 && selectedIds.length === visibleIds.length}
                  onChange={(e) =>
                    setSelected(e.target.checked ? new Set(visibleIds) : new Set())
                  }
                />
              </th>
              <th className="px-4 py-2.5">Email</th>
              <th className="px-4 py-2.5">Name</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Flow</th>
              <th className="px-4 py-2.5">Phase</th>
              <th className="px-4 py-2.5">Last activity</th>
              <th className="px-4 py-2.5">Created</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-500">Loading…</td></tr>
            ) : error ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[var(--danger)]">Failed to load.</td></tr>
            ) : data && data.items.length > 0 ? (
              data.items.map((p) => (
                <tr key={p.id} className="border-b border-[var(--border)] last:border-0 hover:bg-zinc-50">
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      aria-label={`Select ${p.email}`}
                      checked={selected.has(p.id)}
                      onChange={() => toggle(p.id)}
                    />
                  </td>
                  <td className="px-4 py-2.5">
                    <Link to={`/prospects/${p.id}`} className="text-[var(--primary)] hover:underline">
                      {p.email}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5">{p.fullName ?? "—"}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-2.5"><FlowStateBadge state={p.flowState} /></td>
                  <td className="px-4 py-2.5 text-[var(--fg-muted)]">{p.currentPhaseSlug ?? "—"}</td>
                  <td className="px-4 py-2.5 text-[var(--fg-muted)]">{formatDateTime(p.lastActivityAt)}</td>
                  <td className="px-4 py-2.5 text-[var(--fg-muted)]">{formatDateTime(p.createdAt)}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-500">No prospects.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data && data.total > data.size ? (
        <div className="flex items-center justify-between text-sm">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="rounded-md border border-[var(--border)] px-3 py-1.5 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-[var(--fg-muted)]">
            Page {page + 1} of {Math.max(1, Math.ceil(data.total / data.size))}
          </span>
          <button
            disabled={(page + 1) * data.size >= data.total}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-md border border-[var(--border)] px-3 py-1.5 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}

interface CreateBody {
  strategyId: string;
  email: string;
  fullName: string | null;
  phone: string | null;
  timezone: string | null;
  customFields: Record<string, unknown>;
}

/**
 * Modal that creates a prospect. The form is composed of:
 *   1. A fixed header (strategy picker, email, name, phone, timezone).
 *   2. Strategy-specific fields rendered from /prospect-schema. The
 *      backend stores them on prospects.custom_fields and the engine
 *      doesn't interpret them — every label/key/type comes from YAML.
 */
function CreateProspectModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();

  const strategiesQ = useQuery({
    queryKey: ["strategies"],
    queryFn: () => api.get<StrategySummary[]>("/api/v1/strategies"),
  });

  const [strategySlug, setStrategySlug] = useState<string | null>(null);
  useEffect(() => {
    if (strategySlug) return;
    const first = strategiesQ.data?.[0];
    if (first) setStrategySlug(first.slug);
  }, [strategiesQ.data, strategySlug]);

  const strategyId = useMemo(() => {
    if (!strategySlug) return null;
    return strategiesQ.data?.find((s) => s.slug === strategySlug)?.id ?? null;
  }, [strategiesQ.data, strategySlug]);

  const schemaQ = useQuery({
    queryKey: ["prospect-schema", strategySlug],
    enabled: !!strategySlug,
    queryFn: () => api.get<ProspectSchemaView>(`/api/v1/strategies/${strategySlug}/prospect-schema`),
  });

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [timezone, setTimezone] = useState("");
  // Per-field values — string because <input> always returns string; we
  // coerce on submit using the schema's declared type.
  const [fields, setFields] = useState<Record<string, string | boolean>>({});

  const create = useMutation({
    mutationFn: (body: CreateBody) =>
      api.post<ProspectDetail>("/api/v1/prospects", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prospects"] });
      onClose();
    },
  });

  function coerce(field: ProspectFieldView, raw: string | boolean): unknown {
    if (raw === undefined || raw === null || raw === "") return null;
    switch (field.type) {
      case "number": {
        const n = typeof raw === "string" ? parseFloat(raw) : NaN;
        return Number.isFinite(n) ? n : null;
      }
      case "boolean":
        return raw === true || raw === "true";
      default:
        return raw;
    }
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!strategyId || !email.trim()) return;
    const customFields: Record<string, unknown> = {};
    for (const f of schemaQ.data?.fields ?? []) {
      const v = coerce(f, fields[f.key] ?? "");
      if (v !== null && v !== "") customFields[f.key] = v;
    }
    create.mutate({
      strategyId,
      email: email.trim(),
      fullName: fullName.trim() || null,
      phone: phone.trim() || null,
      timezone: timezone.trim() || null,
      customFields,
    });
  }

  const schemaFields = schemaQ.data?.fields ?? [];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/30">
      <div className="bg-white rounded-lg shadow-lg max-w-xl w-full max-h-[90vh] overflow-auto">
        <header className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
          <h2 className="text-lg font-semibold">New prospect</h2>
          <button onClick={onClose} className="text-sm text-zinc-500 hover:text-zinc-900">×</button>
        </header>
        <form onSubmit={submit} className="p-5 space-y-3 text-sm">
          <SmallField label="Strategy">
            <select
              value={strategySlug ?? ""}
              onChange={(e) => setStrategySlug(e.target.value)}
              className="w-full rounded-md border border-[var(--border)] bg-white px-2 py-1"
            >
              {strategiesQ.data?.map((s) => (
                <option key={s.slug} value={s.slug}>{s.name}</option>
              ))}
            </select>
          </SmallField>
          <SmallField label="Email">
            <input value={email} onChange={(e) => setEmail(e.target.value)}
              type="email" required
              className="w-full rounded-md border border-[var(--border)] px-2 py-1" />
          </SmallField>
          <SharedAddressNotice strategyId={strategyId} email={email} variant="form" />
          <div className="grid grid-cols-2 gap-2">
            <SmallField label="Full name">
              <input value={fullName} onChange={(e) => setFullName(e.target.value)}
                className="w-full rounded-md border border-[var(--border)] px-2 py-1" />
            </SmallField>
            <SmallField label="Phone">
              <input value={phone} onChange={(e) => setPhone(e.target.value)}
                className="w-full rounded-md border border-[var(--border)] px-2 py-1" />
            </SmallField>
          </div>
          <SmallField label="Timezone (e.g. America/New_York)">
            <input value={timezone} onChange={(e) => setTimezone(e.target.value)}
              className="w-full rounded-md border border-[var(--border)] px-2 py-1" />
          </SmallField>

          {schemaFields.length > 0 ? (
            <div className="pt-3 mt-3 border-t border-[var(--border)] space-y-3">
              <div className="text-xs uppercase tracking-wide text-[var(--fg-muted)]">
                Strategy-specific fields
              </div>
              {schemaFields.map((f) => (
                <DynamicField
                  key={f.key}
                  field={f}
                  value={fields[f.key]}
                  onChange={(v) => setFields((cur) => ({ ...cur, [f.key]: v }))}
                />
              ))}
            </div>
          ) : null}

          {create.error ? (
            <div className="text-sm text-[var(--danger)]">
              {create.error instanceof ApiError ? `HTTP ${create.error.status}` : "Create failed"}
            </div>
          ) : null}

          <footer className="flex items-center justify-end gap-2 pt-3 border-t border-[var(--border)]">
            <button type="button" onClick={onClose}
              className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm">Cancel</button>
            <button type="submit"
              disabled={!email.trim() || !strategyId || create.isPending}
              className="rounded-md bg-blue-600 text-white px-3 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50"
            >{create.isPending ? "Creating…" : "Create"}</button>
          </footer>
        </form>
      </div>
    </div>
  );
}

function DynamicField({
  field, value, onChange,
}: {
  field: ProspectFieldView;
  value: string | boolean | undefined;
  onChange: (v: string | boolean) => void;
}) {
  const cls = "w-full rounded-md border border-[var(--border)] bg-white px-2 py-1 text-sm";
  return (
    <label className="block">
      <span className="text-xs text-[var(--fg-muted)]">
        {field.label}{field.required ? " *" : ""}
      </span>
      {field.description ? (
        <span className="text-[10px] text-[var(--fg-muted)] block">{field.description}</span>
      ) : null}
      <div className="mt-1">
        {field.type === "boolean" ? (
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
          />
        ) : field.type === "select" && field.options ? (
          <select
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            required={field.required}
            className={cls}
          >
            <option value=""></option>
            {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : field.type === "text" ? (
          <textarea
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            required={field.required}
            rows={3}
            className={cls}
          />
        ) : (
          <input
            type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            required={field.required}
            className={cls}
          />
        )}
      </div>
    </label>
  );
}

function SmallField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-[var(--fg-muted)]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
